import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import useProducts from "../../api/products";
import useCompany from "../../api/company";
import { Input } from "../../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Package,
  ArrowUpDown,
  MoreHorizontal,
  Image as ImageIcon,
  LayoutGrid,
  ArrowLeft,
  GripVertical,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import ProductEditor from "./ProductEditor";
import { useTheme } from "../../context/ThemeContext";

const S3_BASE_URL = process.env.REACT_APP_S3_BASE_URL;

const ShopManager = ({ targetCompanyId = null }) => {
  const { getAllProducts, deleteProduct, updateProduct, loading, getProductsByCompanyId, deleteProductForCompany, updateProductForCompany } =
    useProducts();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [view, setView] = useState("categories"); // "categories" | "products"
  const [customCategories, setCustomCategories] = useState([]);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");

  // Edit Category State
  const [isEditCategoryDialogOpen, setIsEditCategoryDialogOpen] =
    useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState("");

  // --- Volgorde van de categorieën op de website ---------------------------
  // Bewaard als company.shop_settings.categoryOrder (JSON-kolom), zodat de
  // klantensite exact dezelfde volgorde kan aanhouden.
  const {
    getCurrentCompany,
    getAllCompanies: fetchCompanies,
    updateCompany,
  } = useCompany();
  const [companyId, setCompanyId] = useState(targetCompanyId || null);
  const [shopSettings, setShopSettings] = useState({});
  const [categoryOrder, setCategoryOrder] = useState([]);
  const [orderSaving, setOrderSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const pendingOrderRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const loadShopSettings = async () => {
      try {
        let company;
        if (targetCompanyId) {
          const all = await fetchCompanies();
          company = (all || []).find(
            (c) => String(c.id) === String(targetCompanyId),
          );
        } else {
          company = await getCurrentCompany();
        }
        if (cancelled || !company) return;

        let settings = company.shop_settings;
        if (typeof settings === "string") {
          try {
            settings = JSON.parse(settings);
          } catch (e) {
            settings = null;
          }
        }
        setCompanyId(company.id);
        setShopSettings(settings || {});
        setCategoryOrder(
          Array.isArray(settings?.categoryOrder) ? settings.categoryOrder : [],
        );
      } catch (error) {
        console.error("Kon de categorie-volgorde niet laden", error);
      }
    };

    loadShopSettings();
    return () => {
      cancelled = true;
    };
  }, [targetCompanyId, getCurrentCompany, fetchCompanies]);

  const persistCategoryOrder = async (order) => {
    if (!companyId || !Array.isArray(order)) return;
    setOrderSaving(true);
    try {
      const next = { ...shopSettings, categoryOrder: order };
      await updateCompany(companyId, { shop_settings: next });
      setShopSettings(next);
    } catch (error) {
      console.error("Volgorde opslaan mislukt", error);
      alert("Volgorde van de categorieën opslaan mislukt");
    } finally {
      setOrderSaving(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const data = targetCompanyId
        ? await getProductsByCompanyId(targetCompanyId)
        : await getAllProducts();
      setProducts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch products", error);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm("Weet je zeker dat je dit product wilt verwijderen?")) {
      try {
        if (targetCompanyId) {
          await deleteProductForCompany(targetCompanyId, id);
        } else {
          await deleteProduct(id);
        }
        fetchProducts();
      } catch (error) {
        console.error("Failed to delete product", error);
      }
    }
  };

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setIsEditorOpen(true);
  };

  const handleCreate = () => {
    setSelectedProduct(null);
    setIsEditorOpen(true);
  };

  const handleEditorClose = () => {
    setIsEditorOpen(false);
    fetchProducts();
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedProducts = [...products].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const filteredProducts = sortedProducts.filter((product) => {
    const name = product.name?.nl || product.name || "";
    const matchesSearch = name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      categoryFilter === "all" || product.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const categories = [
    "all",
    ...new Set([
      ...products.map((p) => p.category).filter(Boolean),
      ...customCategories,
    ]),
  ];

  const categoryStats = products.reduce((acc, product) => {
    const cat = product.category || "Uncategorized";
    if (!acc[cat]) {
      acc[cat] = { count: 0, image: null };
    }
    acc[cat].count++;
    if (!acc[cat].image && product.images && product.images.length > 0) {
      acc[cat].image = product.images[0];
    }
    return acc;
  }, {});

  customCategories.forEach((cat) => {
    if (!categoryStats[cat]) {
      categoryStats[cat] = { count: 0, image: null };
    }
  });

  const suggestedAttributes = products
    .filter(
      (p) =>
        categoryFilter !== "all" &&
        p.category === categoryFilter &&
        Array.isArray(p.attributes),
    )
    .flatMap((p) => p.attributes.map((a) => a.key))
    .filter((value, index, self) => self.indexOf(value) === index);

  const handleCategoryClick = (category) => {
    setCategoryFilter(category);
    setView("products");
  };

  const handleBackToCategories = () => {
    setCategoryFilter("all");
    setView("categories");
  };

  const handleAddCategory = () => {
    if (newCategoryName.trim()) {
      setCustomCategories([...customCategories, newCategoryName.trim()]);
      setNewCategoryName("");
      setIsCategoryDialogOpen(false);
    }
  };

  const handleEditCategoryClick = (e, category) => {
    e.stopPropagation();
    setEditingCategory(category);
    setEditCategoryName(category);
    setIsEditCategoryDialogOpen(true);
  };

  const handleUpdateCategory = async () => {
    if (!editCategoryName.trim() || editCategoryName === editingCategory) {
      setIsEditCategoryDialogOpen(false);
      return;
    }

    const newName = editCategoryName.trim();
    const oldName = editingCategory;

    try {
      // 1. Update all products with this category
      const productsToUpdate = products.filter((p) => p.category === oldName);

      // We'll do this in parallel for speed, though sequential is safer for errors
      await Promise.all(
        productsToUpdate.map((product) =>
          targetCompanyId
            ? updateProductForCompany(targetCompanyId, product.id, { ...product, category: newName })
            : updateProduct(product.id, { ...product, category: newName }),
        ),
      );

      // 2. Update customCategories if it was an empty category
      if (customCategories.includes(oldName)) {
        setCustomCategories(
          customCategories.map((c) => (c === oldName ? newName : c)),
        );
      }

      // 3. Refresh products
      await fetchProducts();
      setIsEditCategoryDialogOpen(false);
    } catch (error) {
      console.error("Failed to update category", error);
      alert("Categorie bijwerken mislukt");
    }
  };

  const getImageUrl = (images) => {
    if (!images || images.length === 0) return null;
    const imageKey = Array.isArray(images) ? images[0] : images;
    if (imageKey.startsWith("http")) return imageKey;
    return `${S3_BASE_URL}/${imageKey}`;
  };

  const parseNumber = (val) => {
    if (val === null || val === undefined) return 0;
    if (typeof val === "number") return val;

    // Convert to string and remove currency symbols/spaces
    let str = String(val).replace(/[^0-9.,-]/g, "");

    if (!str) return 0;

    // Handle Dutch format (1.000,00) vs US format (1,000.00)
    // If both . and , exist
    if (str.includes(".") && str.includes(",")) {
      // If comma is last, it's likely decimal separator (European)
      if (str.lastIndexOf(",") > str.lastIndexOf(".")) {
        str = str.replace(/\./g, "").replace(",", ".");
      } else {
        // Comma is likely thousands separator (US)
        str = str.replace(/,/g, "");
      }
    } else if (str.includes(",")) {
      // Only comma exists. Assume it's decimal separator
      str = str.replace(",", ".");
    }

    return parseFloat(str) || 0;
  };

  // Categorieën die nog niet in categoryOrder staan (net toegevoegd) komen
  // achteraan, alfabetisch.
  const sortByCategoryOrder = (names) => {
    const rank = new Map(categoryOrder.map((name, i) => [name, i]));
    return [...names].sort((a, b) => {
      const ra = rank.has(a) ? rank.get(a) : Number.MAX_SAFE_INTEGER;
      const rb = rank.has(b) ? rank.get(b) : Number.MAX_SAFE_INTEGER;
      if (ra !== rb) return ra - rb;
      return a.localeCompare(b, "nl");
    });
  };

  const orderedCategories = sortByCategoryOrder(Object.keys(categoryStats));

  const handleOrderDragOver = (e, index) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    const next = [...orderedCategories];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(index, 0, moved);
    pendingOrderRef.current = next;
    setDragIndex(index);
    setCategoryOrder(next);
  };

  const handleOrderDrop = () => {
    setDragIndex(null);
    const order = pendingOrderRef.current;
    pendingOrderRef.current = null;
    if (order) persistCategoryOrder(order);
  };

  const totalValue = products.reduce(
    (acc, p) => acc + parseNumber(p.price) * parseNumber(p.stock),
    0,
  );
  const lowStockCount = products.filter((p) => {
    const stock = parseNumber(p.stock);
    return stock < 5;
  }).length;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div
          className={`rounded-xl p-4 ${isDark ? "bg-white/[0.04] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
        >
          <span
            className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-400"}`}
          >
            Totaal Producten
          </span>
          <p
            className={`text-xl font-semibold mt-1 ${isDark ? "text-white" : "text-gray-900"}`}
          >
            {products.length}
          </p>
        </div>
        <div
          className={`rounded-xl p-4 ${isDark ? "bg-white/[0.04] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
        >
          <span
            className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-400"}`}
          >
            Voorraadwaarde
          </span>
          <p
            className={`text-xl font-semibold mt-1 ${isDark ? "text-white" : "text-gray-900"}`}
          >
            €{" "}
            {totalValue.toLocaleString("nl-NL", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </p>
        </div>
        <div
          className={`rounded-xl p-4 ${isDark ? "bg-white/[0.04] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
        >
          <span
            className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-400"}`}
          >
            Lage Voorraad
          </span>
          <p
            className={`text-xl font-semibold mt-1 ${lowStockCount > 0 ? "text-orange-500" : isDark ? "text-white" : "text-gray-900"}`}
          >
            {lowStockCount}
          </p>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          {view === "products" && (
            <button
              onClick={handleBackToCategories}
              className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-900 hover:bg-gray-100"}`}
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
          <div>
            <h2
              className={`text-[15px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
            >
              {view === "categories"
                ? "Winkel Categorieën"
                : categoryFilter === "all"
                  ? "Alle Producten"
                  : categoryFilter}
            </h2>
            <p
              className={`text-[12px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
            >
              {view === "categories"
                ? "Selecteer een categorie om producten te beheren."
                : `Beheer producten in ${categoryFilter === "all" ? "je winkel" : categoryFilter}.`}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleCreate}
            className={`h-8 px-3 rounded-lg text-[12px] font-medium flex items-center gap-1.5 transition-colors ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
          >
            <Plus className="h-3.5 w-3.5" /> Product
          </button>
          {view === "categories" && (
            <button
              onClick={() => setIsCategoryDialogOpen(true)}
              className={`h-8 px-3 rounded-lg text-[12px] font-medium flex items-center gap-1.5 border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
            >
              <Plus className="h-3.5 w-3.5" /> Categorie
            </button>
          )}
        </div>
      </div>

      {view === "categories" && orderedCategories.length > 1 && (
        <div
          className={`rounded-xl p-4 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h3
                className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                Volgorde categorieën
              </h3>
              <p
                className={`text-[12px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                Sleep om te bepalen in welke volgorde ze op de website staan.
              </p>
            </div>
            {orderSaving && (
              <span
                className={`text-[11px] shrink-0 ${isDark ? "text-gray-500" : "text-gray-400"}`}
              >
                Opslaan...
              </span>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            {orderedCategories.map((cat, i) => (
              <div
                key={cat}
                draggable
                onDragStart={() => setDragIndex(i)}
                onDragOver={(e) => handleOrderDragOver(e, i)}
                onDrop={handleOrderDrop}
                onDragEnd={handleOrderDrop}
                className={`flex items-center gap-2.5 h-9 px-2.5 rounded-lg text-[12px] cursor-grab active:cursor-grabbing transition-colors ${
                  dragIndex === i
                    ? isDark
                      ? "bg-white/[0.08]"
                      : "bg-gray-100"
                    : isDark
                      ? "hover:bg-white/[0.04]"
                      : "hover:bg-gray-50"
                }`}
              >
                <GripVertical
                  className={`h-3.5 w-3.5 shrink-0 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                />
                <span
                  className={`w-4 tabular-nums ${isDark ? "text-gray-600" : "text-gray-400"}`}
                >
                  {i + 1}
                </span>
                <span
                  className={`font-medium truncate ${isDark ? "text-gray-200" : "text-gray-800"}`}
                >
                  {cat}
                </span>
                <span
                  className={`ml-auto shrink-0 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                >
                  {categoryStats[cat]?.count ?? 0} producten
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {view === "categories" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {/* All Products card */}
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-xl p-5 cursor-pointer transition-colors flex flex-col items-center justify-center gap-3 h-44 group ${isDark ? "bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.05]" : "bg-white border border-gray-200/80 hover:bg-gray-50"}`}
            onClick={() => handleCategoryClick("all")}
          >
            <div
              className={`h-14 w-14 rounded-full flex items-center justify-center ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
            >
              <Package
                className={`h-6 w-6 ${isDark ? "text-gray-400" : "text-gray-500"}`}
              />
            </div>
            <div className="text-center">
              <h3
                className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                Alle Producten
              </h3>
              <p
                className={`text-[12px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                {products.length} Producten
              </p>
            </div>
          </motion.div>

          {orderedCategories.map((cat, i) => {
            const stats = categoryStats[cat] || { count: 0, image: null };
            return (
            <motion.div
              key={cat}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: (i + 1) * 0.03 }}
              className={`rounded-xl p-5 cursor-pointer transition-colors flex flex-col items-center justify-center gap-3 h-44 relative group ${isDark ? "bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.05]" : "bg-white border border-gray-200/80 hover:bg-gray-50"}`}
              onClick={() => handleCategoryClick(cat)}
            >
              <div
                className={`h-14 w-14 rounded-full flex items-center justify-center overflow-hidden ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
              >
                {getImageUrl(stats.image) ? (
                  <img
                    src={getImageUrl(stats.image)}
                    alt={cat}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <LayoutGrid
                    className={`h-6 w-6 ${isDark ? "text-gray-400" : "text-gray-500"}`}
                  />
                )}
              </div>
              <div className="text-center">
                <h3
                  className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  {cat}
                </h3>
                <p
                  className={`text-[12px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                >
                  {stats.count} Producten
                </p>
              </div>
              <button
                className={`absolute top-2 right-2 h-7 w-7 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"}`}
                onClick={(e) => handleEditCategoryClick(e, cat)}
              >
                <Pencil className="h-3 w-3" />
              </button>
            </motion.div>
            );
          })}
        </div>
      ) : (
        <>
          {/* Search & Filter */}
          <div
            className={`flex flex-col sm:flex-row gap-3 items-center justify-between rounded-xl p-3 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
          >
            <div className="relative w-full sm:w-80">
              <Search
                className={`absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
              />
              <Input
                placeholder="Zoek producten..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-8 h-9 text-[13px] rounded-lg ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"}`}
              />
            </div>
            <select
              className={`h-9 rounded-lg px-3 text-[13px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-gray-50 border-gray-200 text-gray-900"}`}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat === "all" ? "Alle Categorieën" : cat}
                </option>
              ))}
            </select>
          </div>

          {/* Product Table */}
          {/* Mobile cards */}
          <div className="sm:hidden space-y-3">
            {filteredProducts.length === 0 ? (
              <div
                className={`rounded-xl p-8 text-center ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
              >
                <Package
                  className={`h-8 w-8 mx-auto mb-2 ${isDark ? "text-gray-700" : "text-gray-300"}`}
                />
                <p
                  className={`text-[13px] ${isDark ? "text-gray-500" : "text-gray-400"}`}
                >
                  Geen producten gevonden.
                </p>
              </div>
            ) : (
              filteredProducts.map((product, i) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.02 }}
                  className={`rounded-xl overflow-hidden ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
                >
                  <div className="p-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={`h-11 w-11 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center ${isDark ? "bg-white/[0.04]" : "bg-gray-50 border border-gray-100"}`}
                      >
                        {getImageUrl(product.images) ? (
                          <img
                            src={getImageUrl(product.images)}
                            alt={product.name?.nl || product.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <ImageIcon
                            className={`h-4 w-4 ${isDark ? "text-gray-600" : "text-gray-300"}`}
                          />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-[13px] font-semibold truncate ${isDark ? "text-white" : "text-gray-900"}`}
                        >
                          {product.name?.nl ||
                            product.name ||
                            "Naamloos Product"}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span
                            className={`text-[12px] font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                          >
                            € {Number(product.price).toFixed(2)}
                          </span>
                          <span
                            className={`text-[11px] ${
                              parseNumber(product.stock) > 10
                                ? isDark
                                  ? "text-green-400"
                                  : "text-green-600"
                                : parseNumber(product.stock) > 0
                                  ? isDark
                                    ? "text-orange-400"
                                    : "text-orange-500"
                                  : isDark
                                    ? "text-red-400"
                                    : "text-red-500"
                            }`}
                          >
                            {product.stock} op voorraad
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => handleEdit(product)}
                          className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-400 active:bg-white/[0.06]" : "text-gray-500 active:bg-gray-100"}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => handleDelete(product.id)}
                          className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-600 active:bg-red-500/15" : "text-gray-400 active:bg-red-50"}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>

          {/* Desktop table */}
          <div
            className={`hidden sm:block rounded-xl border overflow-hidden ${isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
          >
            <Table>
              <TableHeader>
                <TableRow
                  className={isDark ? "border-white/[0.06]" : "border-gray-100"}
                >
                  <TableHead
                    className={`w-[60px] text-[11px] font-semibold uppercase tracking-wider ${isDark ? "text-gray-500 bg-white/[0.02]" : "text-gray-400 bg-gray-50/50"}`}
                  >
                    Img
                  </TableHead>
                  {["name", "category", "price", "stock"].map((key) => (
                    <TableHead
                      key={key}
                      className={`cursor-pointer text-[11px] font-semibold uppercase tracking-wider transition-colors ${isDark ? "text-gray-500 bg-white/[0.02] hover:bg-white/[0.04]" : "text-gray-400 bg-gray-50/50 hover:bg-gray-100"}`}
                      onClick={() => handleSort(key)}
                    >
                      <div className="flex items-center gap-1">
                        {key === "name"
                          ? "Naam"
                          : key === "category"
                            ? "Categorie"
                            : key === "price"
                              ? "Prijs"
                              : "Voorraad"}
                        <ArrowUpDown className="h-3 w-3 opacity-40" />
                      </div>
                    </TableHead>
                  ))}
                  <TableHead
                    className={`text-right text-[11px] font-semibold uppercase tracking-wider ${isDark ? "text-gray-500 bg-white/[0.02]" : "text-gray-400 bg-gray-50/50"}`}
                  >
                    Acties
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="h-32 text-center">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <Package
                          className={`h-8 w-8 ${isDark ? "text-gray-700" : "text-gray-300"}`}
                        />
                        <p
                          className={`text-[13px] ${isDark ? "text-gray-500" : "text-gray-400"}`}
                        >
                          Geen producten gevonden.
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProducts.map((product) => (
                    <TableRow
                      key={product.id}
                      className={`transition-colors ${isDark ? "border-white/[0.04] hover:bg-white/[0.02]" : "border-gray-50 hover:bg-gray-50/50"}`}
                    >
                      <TableCell>
                        <div
                          className={`h-10 w-10 rounded-lg overflow-hidden flex items-center justify-center ${isDark ? "bg-white/[0.04]" : "bg-gray-50 border border-gray-100"}`}
                        >
                          {getImageUrl(product.images) ? (
                            <img
                              src={getImageUrl(product.images)}
                              alt={product.name?.nl || product.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <ImageIcon
                              className={`h-4 w-4 ${isDark ? "text-gray-600" : "text-gray-300"}`}
                            />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span
                            className={`text-[13px] font-medium truncate max-w-[180px] block ${isDark ? "text-white" : "text-gray-900"}`}
                          >
                            {product.name?.nl ||
                              product.name ||
                              "Naamloos Product"}
                          </span>
                          {product.sku && (
                            <span
                              className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                            >
                              {product.sku}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span
                          className={`text-[12px] px-2 py-0.5 rounded-md ${isDark ? "bg-white/[0.06] text-gray-300" : "bg-gray-100 text-gray-600"}`}
                        >
                          {product.category || "—"}
                        </span>
                      </TableCell>
                      <TableCell
                        className={`text-[13px] font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                      >
                        € {Number(product.price).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`text-[12px] font-medium ${
                            parseNumber(product.stock) > 10
                              ? isDark
                                ? "text-green-400"
                                : "text-green-600"
                              : parseNumber(product.stock) > 0
                                ? isDark
                                  ? "text-orange-400"
                                  : "text-orange-500"
                                : isDark
                                  ? "text-red-400"
                                  : "text-red-500"
                          }`}
                        >
                          {product.stock}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className={`h-7 w-7 rounded-lg inline-flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"}`}
                            >
                              <MoreHorizontal className="h-3.5 w-3.5" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className={`min-w-[140px] rounded-lg ${isDark ? "bg-[#1a1a1a] border-white/[0.08]" : ""}`}
                          >
                            <DropdownMenuItem
                              onClick={() => handleEdit(product)}
                              className={`text-[12px] ${isDark ? "text-gray-300 focus:bg-white/[0.06]" : ""}`}
                            >
                              <Pencil className="mr-2 h-3.5 w-3.5" /> Bewerken
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className={`text-[12px] text-red-500 focus:text-red-500 ${isDark ? "focus:bg-red-500/10" : "focus:bg-red-50"}`}
                              onClick={() => handleDelete(product.id)}
                            >
                              <Trash2 className="mr-2 h-3.5 w-3.5" />{" "}
                              Verwijderen
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {/* Product Editor Dialog */}
      <Dialog open={isEditorOpen} onOpenChange={setIsEditorOpen}>
        <DialogContent
          className={`max-w-4xl max-h-[90vh] overflow-y-auto rounded-xl border p-0 ${isDark ? "bg-[#141414] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
        >
          <div
            className={`px-6 py-4 border-b ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
          >
            <h3
              className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
            >
              {selectedProduct ? "Product Bewerken" : "Product Aanmaken"}
            </h3>
          </div>
          <div className="p-6">
            <ProductEditor
              product={selectedProduct}
              onClose={handleEditorClose}
              initialCategory={categoryFilter !== "all" ? categoryFilter : ""}
              suggestedAttributes={suggestedAttributes}
              targetCompanyId={targetCompanyId}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Category Dialog */}
      <Dialog
        open={isCategoryDialogOpen}
        onOpenChange={setIsCategoryDialogOpen}
      >
        <DialogContent
          className={`sm:max-w-sm rounded-xl border p-6 ${isDark ? "bg-[#141414] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
        >
          <h3
            className={`text-[14px] font-semibold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}
          >
            Nieuwe Categorie
          </h3>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label
                className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
              >
                Categorie Naam
              </label>
              <Input
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="bv. Zomer Collectie"
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddCategory();
                }}
                className={`h-9 text-[13px] rounded-lg ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"}`}
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setIsCategoryDialogOpen(false)}
                className={`h-8 px-3 rounded-lg text-[12px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
              >
                Annuleren
              </button>
              <button
                onClick={handleAddCategory}
                className={`h-8 px-3 rounded-lg text-[12px] font-medium transition-colors ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
              >
                Toevoegen
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog
        open={isEditCategoryDialogOpen}
        onOpenChange={setIsEditCategoryDialogOpen}
      >
        <DialogContent
          className={`sm:max-w-sm rounded-xl border p-6 ${isDark ? "bg-[#141414] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
        >
          <h3
            className={`text-[14px] font-semibold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}
          >
            Categorie Bewerken
          </h3>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label
                className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
              >
                Categorie Naam
              </label>
              <Input
                value={editCategoryName}
                onChange={(e) => setEditCategoryName(e.target.value)}
                placeholder="Nieuwe naam"
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleUpdateCategory();
                }}
                className={`h-9 text-[13px] rounded-lg ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"}`}
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setIsEditCategoryDialogOpen(false)}
                className={`h-8 px-3 rounded-lg text-[12px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
              >
                Annuleren
              </button>
              <button
                onClick={handleUpdateCategory}
                className={`h-8 px-3 rounded-lg text-[12px] font-medium transition-colors ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
              >
                Opslaan
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShopManager;
